// client/src/search/normalize.js

export const deUmlaut = (s = "") =>
  String(s)
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue")
    .replace(/Ä/g, "ae").replace(/Ö/g, "oe").replace(/Ü/g, "ue")
    .replace(/ß/g, "ss");

export const norm = (v) =>
  deUmlaut(String(v ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""))
    .replace(/[^a-z0-9.]/g, " ")     // Punkt für dd.mm.yyyy behalten
    .replace(/\s+/g, " ")
    .trim();

const STOPWORDS = new Set([
  "der", "die", "das", "den", "dem", "des", "ein", "eine", "einer", "eines",
  "und", "oder", "im", "in", "am", "an", "auf", "bei", "von", "zum", "zur",
  "mit", "ohne", "für", "fuer",
]);

/** Rechnungs-Präfixe: „RG 5023145“ → nur Ziffern-Token matchen */
const INVOICE_PREFIX_TOKENS = new Set([
  "rg", "rechnung", "rechnungsnr", "rechnungsnummer", "inv", "invoice", "proforma", "nr",
]);

function pad2(n) {
  return String(n).padStart(2, "0");
}

function isYear(x) { return x >= 1900 && x <= 2100; }
function isMonth(x) { return x >= 1 && x <= 12; }
function isDay(x) { return x >= 1 && x <= 31; }

function extractSpecials(raw) {
  const specials = new Set();
  const s = String(raw ?? "");

  // kw12 / kw 12
  s.replace(/\bkw\s?(\d{1,2})\b/ig, (_, n) => specials.add(`kw${Number(n)}`));

  // ISO yyyy-mm-dd
  s.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (m) => specials.add(m.toLowerCase()));

  // dd.mm.yyyy / dd-mm-yyyy / dd/mm/yyyy
  s.replace(/\b(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})\b/g, (_, dd, mm, yyyy) => {
    const D = Number(dd), M = Number(mm), Y = Number(yyyy);
    if (isYear(Y) && isMonth(M) && isDay(D)) {
      specials.add(`${Y}-${pad2(M)}-${pad2(D)}`);
      specials.add(`${D}.${M}.`); // optional: Tag/Monat-Token
      specials.add(`${pad2(D)}.${pad2(M)}.${Y}`); // original-ähnlich (für HardToken)
    }
    return _;
  });

  // dd.mm. / dd-mm / dd/mm  (ohne Jahr)
  s.replace(/\b(\d{1,2})[.\-/](\d{1,2})[.\-/]?\b/g, (_, dd, mm) => {
    const D = Number(dd), M = Number(mm);
    if (isMonth(M) && isDay(D)) specials.add(`${D}.${M}.`);
    return _;
  });

  return [...specials].map((x) => String(x).toLowerCase());
}

/**
 * tokenize(q)
 * - robust für raw input UND bereits norm()-Input ("2025 12 22")
 * - entfernt Stopwords + 1-char tokens
 * - merged "diese woche" -> "diese_woche" etc.
 * - erkennt Datums-Triples in normalisierten Tokens und entfernt deren Zahlen-Tokens
 */
export const tokenize = (q) => {
  const raw = String(q ?? "");

  // 1) Specials aus RAW ziehen (KW / echte Formate)
  const specials = new Set(extractSpecials(raw));

  // 2) Text normalisieren
  const parts = norm(raw).split(" ").filter(Boolean);

  // 3) Numeric Triple Detection auf parts (für Fälle, wo vorher norm() drüber lief)
  //    Beispiel: "2025 12 22" oder "22 12 2025"
  const skip = new Set();
  for (let i = 0; i < parts.length - 2; i++) {
    const a = parts[i], b = parts[i + 1], c = parts[i + 2];
    if (!/^\d+$/.test(a) || !/^\d+$/.test(b) || !/^\d+$/.test(c)) continue;

    const A = Number(a), B = Number(b), C = Number(c);

    // yyyy mm dd
    if (isYear(A) && isMonth(B) && isDay(C)) {
      specials.add(`${A}-${pad2(B)}-${pad2(C)}`);
      skip.add(i); skip.add(i + 1); skip.add(i + 2);
      continue;
    }

    // dd mm yyyy
    if (isDay(A) && isMonth(B) && isYear(C)) {
      specials.add(`${C}-${pad2(B)}-${pad2(A)}`);
      specials.add(`${A}.${B}.`);
      skip.add(i); skip.add(i + 1); skip.add(i + 2);
      continue;
    }
  }

  // 4) Phrase merge + skip numeric triples
  const merged = [];
  for (let i = 0; i < parts.length; i++) {
    if (skip.has(i)) continue;

    const a = parts[i];
    const b = parts[i + 1];

    if (a === "diese" && b === "woche") { merged.push("diese_woche"); i++; continue; }
    if (a === "naechste" && b === "woche") { merged.push("naechste_woche"); i++; continue; }
    if (a === "letzte" && b === "woche") { merged.push("letzte_woche"); i++; continue; }

    if (a === "kw" && /^\d{1,2}$/.test(b)) { merged.push(`kw${Number(b)}`); i++; continue; }

    merged.push(a);
  }

  // 5) Stopwords + 1-char tokens raus
  const filtered = merged.filter((t) => {
    if (!t) return false;
    if (t.includes("_")) return true;
    if (STOPWORDS.has(t)) return false;
    if (INVOICE_PREFIX_TOKENS.has(t)) return false;
    if (t.length === 1 && !/^\d$/.test(t)) return false;
    return true;
  });

  // 6) Combine unique
  const out = new Set(filtered);
  for (const s of specials) out.add(s);

  return [...out];
};
